import {
  MARKETPLACE_ORDER_HANDOFF_OVERRIDE_NOTE_MAX,
  type TargetRatio,
} from "@nexus/contracts";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Skeleton,
  Textarea,
  cn,
} from "@nexus/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  adminOrderPartnerOptionsQueryOptions,
  adminOrderScriptAdPlatformsQueryOptions,
} from "@/lib/queries";

import { VendorIdentity } from "../../components/orders-list/VendorIdentity";
import { TargetRatiosPicker } from "../../components/TargetRatiosPicker";
import { useTargetRatioSelection } from "../../components/use-target-ratio-selection";
import type { OrderPartnerOption } from "../../types/marketplace.types";

export interface SendToPartnerSubmission {
  tenantVendorId: string;
  handoffOverrideNote?: string;
  targetRatios?: TargetRatio[];
}

/** The source order was saved without target ratios, so the copy needs the admin to pick them. */
export function isMissingTargetRatios(order: {
  handoff?: { targetRatios: readonly TargetRatio[] } | null;
}): boolean {
  return (order.handoff?.targetRatios.length ?? 0) === 0;
}

interface SendToPartnerDialogProps {
  orderId: string;
  orderScriptId: string;
  clientName: string;
  scriptTitle: string;
  /** The source order has no stored target ratios, so the admin must pick them for the copy. */
  needsTargetRatios?: boolean;
  /** A previous send of this line that never succeeded; reopening must resubmit it unchanged to replay. */
  initialSubmission?: SendToPartnerSubmission;
  pending: boolean;
  onSubmit: (submission: SendToPartnerSubmission) => void;
  onClose: () => void;
}

export function SendToPartnerDialog({
  orderId,
  orderScriptId,
  clientName,
  scriptTitle,
  needsTargetRatios = false,
  initialSubmission,
  pending,
  onSubmit,
  onClose,
}: SendToPartnerDialogProps) {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<string | null>(
    initialSubmission?.tenantVendorId ?? null,
  );
  const [note, setNote] = useState(initialSubmission?.handoffOverrideNote ?? "");
  const adPlatformsQuery = useQuery({
    ...adminOrderScriptAdPlatformsQueryOptions(orderId, orderScriptId),
    enabled: needsTargetRatios,
  });
  const ratioSelection = useTargetRatioSelection({
    open: needsTargetRatios,
    adPlatforms: adPlatformsQuery.data ?? null,
    platformsReady:
      !adPlatformsQuery.isLoading &&
      (adPlatformsQuery.isError || adPlatformsQuery.data !== undefined),
    initialSelected: initialSubmission?.targetRatios,
  });
  const hasRequiredRatios = !needsTargetRatios || ratioSelection.selected.length > 0;
  const partnersQuery = useQuery(adminOrderPartnerOptionsQueryOptions(orderId));
  const partners = partnersQuery.data ?? [];
  // A restored partner may have disconnected or stopped receiving orders since the failed send.
  const sendableId = partners.some(
    (partner) => partner.tenantVendorId === selectedId && partner.canReceiveOrders,
  )
    ? selectedId
    : null;
  const isPartnerListCurrent = partnersQuery.isSuccess && !partnersQuery.isFetching;

  const handleSubmit = () => {
    if (!sendableId || !isPartnerListCurrent || !hasRequiredRatios) return;
    const trimmedNote = note.trim();
    onSubmit({
      tenantVendorId: sendableId,
      ...(trimmedNote ? { handoffOverrideNote: trimmedNote } : {}),
      ...(needsTargetRatios ? { targetRatios: ratioSelection.selected } : {}),
    });
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("marketplace.orders.sendToPartner.title", "Send to another partner")}
          </DialogTitle>
          <DialogDescription>
            {t("marketplace.orders.sendToPartner.description", {
              title: scriptTitle,
              client: clientName,
              defaultValue:
                "A copy of “{{title}}” goes to the partner you pick as a new {{client}} order.",
            })}
          </DialogDescription>
        </DialogHeader>
        <PartnerChoices
          loading={partnersQuery.isLoading}
          failed={partnersQuery.isError}
          partners={partners}
          selectedId={sendableId}
          onSelect={setSelectedId}
        />
        {needsTargetRatios ? (
          <div className="flex flex-col gap-2">
            <p className="text-xs text-muted-foreground">
              {t(
                "marketplace.orders.sendToPartner.ratiosHint",
                "This order has no target ratios saved. Pick the cuts the new partner should deliver.",
              )}
            </p>
            <TargetRatiosPicker
              family={ratioSelection.family}
              selected={ratioSelection.selected}
              onToggle={ratioSelection.toggle}
              isReady={ratioSelection.isReady}
              showEmptyError={false}
              disabled={pending}
            />
          </div>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="send-to-partner-note">
            {t("marketplace.orders.sendToPartner.noteLabel", "Note for the partner (optional)")}
          </Label>
          <Textarea
            id="send-to-partner-note"
            value={note}
            maxLength={MARKETPLACE_ORDER_HANDOFF_OVERRIDE_NOTE_MAX}
            onChange={(event) => setNote(event.target.value)}
            rows={3}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            disabled={!sendableId || !isPartnerListCurrent || !hasRequiredRatios || pending}
            onClick={handleSubmit}
          >
            {t("marketplace.orders.sendToPartner.submit", "Send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PartnerChoices({
  loading,
  failed,
  partners,
  selectedId,
  onSelect,
}: {
  loading: boolean;
  failed: boolean;
  partners: OrderPartnerOption[];
  selectedId: string | null;
  onSelect: (tenantVendorId: string) => void;
}) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="flex flex-col gap-2" aria-hidden>
        <Skeleton className="h-11 w-full" />
        <Skeleton className="h-11 w-full" />
      </div>
    );
  }
  if (failed) {
    return (
      <p role="alert" className="text-sm text-error">
        {t("marketplace.orders.sendToPartner.loadError", "Couldn't load this client's partners.")}
      </p>
    );
  }
  if (partners.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t(
          "marketplace.orders.sendToPartner.empty",
          "This client isn't connected to any other partner yet.",
        )}
      </p>
    );
  }
  return (
    <div
      role="radiogroup"
      aria-label={t("marketplace.orders.sendToPartner.partnerLabel", "Partner")}
      className="flex flex-col gap-2"
    >
      {partners.map((partner) => {
        const isSelected = partner.tenantVendorId === selectedId;
        return (
          <button
            key={partner.tenantVendorId}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={!partner.canReceiveOrders}
            onClick={() => onSelect(partner.tenantVendorId)}
            className={cn(
              "flex min-h-11 items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors duration-150 ease-out motion-reduce:transition-none",
              isSelected
                ? "border-primary bg-primary/5"
                : "border-border hover:bg-muted/40",
              "disabled:cursor-not-allowed disabled:opacity-60",
            )}
          >
            <VendorIdentity name={partner.vendor.name} logoUrl={partner.vendor.logoUrl} />
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-medium text-foreground">
                {partner.vendor.name}
              </span>
              {partner.canReceiveOrders ? null : (
                <span className="text-caption text-muted-foreground">
                  {t(
                    "marketplace.orders.sendToPartner.unavailable",
                    "Can't receive orders right now",
                  )}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
